/**
 * P1-1 数据看板深化 —— 统一的数据分析页 /reports
 *
 * 比 /director 更深的多维分析：
 *   - 概览：客户/成交/营收/客单价 + MoM/YoY
 *   - 漏斗各阶段转化率 + 平均停留天数
 *   - 渠道 ROI（按客户数排序）
 *   - 健康度分布
 *   - 团队对比
 *   - 月度趋势 + 本月预测
 *   - 销售员多维排行
 * 顶部筛选：时间范围 / 团队 / 渠道
 * Excel 导出：一键下载全部维度 workbook
 *
 * 权限：admin/director 全权 + manager 看本团队（接口里已自动按 actor.role 限定）
 */
import { useMemo, useState } from 'react';
import { Select, DatePicker, Empty } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { $ } from '../lib/api';
import { useAuthStore } from '../store/auth';
import {
  PageHeader, StatsRow, StatCard, HomeCard, FunnelRow,
  TableWrap, CustCell, Pill, Btn, STAGE_SHORT,
} from '../components/ui';
import { formatMoney, formatInt } from '../lib/format';

// inc6：阶段标签统一改用 ui.tsx 的 STAGE_SHORT（含 L0/L5a/L5b 完整 10 档）。
//   本页漏斗本就直接渲染后端各阶段行(不合并/不丢档)，仅补全 L0/L5a/L5b 标签、删本地字典防漂移。
const STAGE_LABELS = STAGE_SHORT;

const HEALTH_COLOR: Record<string, string> = {
  '90-100': 'var(--success)',
  '70-89': '#52c41a',
  '50-69': '#faad14',
  '30-49': '#fa8c16',
  '0-29': 'var(--danger)',
};

const CHANNEL_OPTIONS = [
  { value: '', label: '全部渠道' },
  { value: 'xiaohongshu', label: '小红书' },
  { value: 'douyin', label: '抖音' },
  { value: 'phone', label: '电话' },
  { value: 'wechat', label: '微信' },
  { value: 'recommend', label: '推荐' },
  { value: 'pool', label: '公海' },
];

export function ReportsPage() {
  const user = useAuthStore((s) => s.user)!;
  const isManager = user.role === 'manager';

  // 默认 "本月"
  const [from, setFrom] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [to, setTo] = useState<Dayjs | null>(dayjs().endOf('month'));
  // 教训 #11 续：manager 的团队约束由 JWT actor scope 在后端兜底，前端不要
  // 显式 setTeamId(user.teamId) —— 否则查询会带 ?teamId=2 → 后端 c.team_id=2 过滤
  //（生产数据 customer.team_id 全 NULL → 0 命中）。team 下拉只给 admin/director 切视角用。
  const [teamId, setTeamId] = useState<number | null>(null);
  const [channel, setChannel] = useState<string>('');

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (from) p.from = from.format('YYYY-MM-DD');
    if (to) p.to = to.format('YYYY-MM-DD');
    if (teamId) p.teamId = String(teamId);
    if (channel) p.channel = channel;
    return p;
  }, [from, to, teamId, channel]);

  const qs = useMemo(() => new URLSearchParams(params).toString(), [params]);
  const path = (p: string) => qs ? `${p}?${qs}` : p;

  const { data: teamsList } = useQuery({
    queryKey: ['teams'],
    queryFn: () => $.get<any[]>('/teams'),
    enabled: !isManager,
  });
  const { data: funnel } = useQuery<any[]>({
    queryKey: ['reports/funnel-detailed', params],
    queryFn: () => $.get(path('/reports/funnel-detailed')),
  });
  const { data: roi } = useQuery<any[]>({
    queryKey: ['reports/channel-roi'],
    queryFn: () => $.get('/reports/channel-roi'),
    enabled: !isManager,
  });
  const { data: health } = useQuery<any[]>({
    queryKey: ['reports/health-buckets', params],
    queryFn: () => $.get(path('/reports/health-buckets')),
  });
  const { data: teamCmp } = useQuery<any[]>({
    queryKey: ['reports/team-comparison', params],
    queryFn: () => $.get(path('/reports/team-comparison')),
    enabled: !isManager,
  });
  const { data: trend } = useQuery<any>({
    queryKey: ['reports/revenue-trend'],
    queryFn: () => $.get('/reports/revenue-trend'),
  });
  const { data: ranking } = useQuery<any[]>({
    queryKey: ['reports/sales-ranking-multi', params],
    queryFn: () => $.get(path('/reports/sales-ranking-multi')),
  });

  const cur = trend?.currentMonth ?? { revenue: 0, deals: 0 };
  const mom = trend?.momPct;
  const yoy = trend?.yoyPct;
  const prediction = trend?.prediction ?? 0;

  const totalCustomers = (teamCmp ?? []).reduce((s, t) => s + t.customers, 0)
    || (ranking ?? []).reduce((s, r) => s + r.customers, 0);
  const avgDeal = cur.deals > 0 ? cur.revenue / cur.deals : 0;

  const exportUrl = `/api/reports/export.xlsx${qs ? `?${qs}` : ''}`;
  const handleExport = async () => {
    // 用 fetch + blob 才能带 Authorization header；axios 直接 GET 也行但需要 responseType:'blob'
    const token = useAuthStore.getState().accessToken;
    const r = await fetch(exportUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      alert(`导出失败：${r.status}`);
      return;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reports-${from?.format('YYYYMM')}.xlsx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <PageHeader
        breadcrumb={['HuanKe', '数据看板']}
        title="数据看板"
        subtitle="销售运营多维分析 · 漏斗/渠道/团队/销售员"
        actions={
          <Btn kind="primary" onClick={handleExport}>导出 Excel</Btn>
        }
      />

      {/* 筛选栏 */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, padding: '12px 16px',
        background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>时间范围:</span>
        <DatePicker.RangePicker
          value={[from, to] as any}
          onChange={(v) => {
            setFrom(v?.[0] ?? null);
            setTo(v?.[1] ?? null);
          }}
          allowClear={false}
          presets={[
            { label: '本月', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
            { label: '上月', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
            // dayjs 默认不带 quarter 插件，本季用 month 推算（当月 % 3 = 季内偏移）
            { label: '本季', value: (() => {
              const m = dayjs().month();
              const qStart = m - (m % 3);
              return [dayjs().month(qStart).startOf('month'), dayjs().month(qStart + 2).endOf('month')];
            })() },
            { label: '本年', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
            { label: '近 30 天', value: [dayjs().subtract(30, 'day'), dayjs()] },
          ]}
        />
        {!isManager && (
          <>
            <span style={{ fontSize: 13, color: 'var(--text-3)', marginLeft: 8 }}>团队:</span>
            <Select
              value={teamId ?? 'all'}
              style={{ width: 140 }}
              onChange={(v) => setTeamId(v === 'all' ? null : Number(v))}
              options={[
                { value: 'all', label: '全公司' },
                ...((teamsList ?? []).map((t: any) => ({ value: Number(t.id), label: t.name }))),
              ]}
            />
          </>
        )}
        <span style={{ fontSize: 13, color: 'var(--text-3)', marginLeft: 8 }}>渠道:</span>
        <Select
          value={channel}
          style={{ width: 140 }}
          onChange={(v) => setChannel(v)}
          options={CHANNEL_OPTIONS}
        />
      </div>

      <div className="work-content">
        {/* 核心指标 */}
        <StatsRow>
          <StatCard label="客户总数" value={formatInt(totalCustomers)} unit="人" />
          <StatCard label="本月成交" value={formatInt(cur.deals)} unit="单"
                    delta={mom !== null && mom !== undefined ? `MoM ${(mom * 100).toFixed(1)}%` : undefined}
                    deltaDir={mom && mom > 0 ? 'up' : mom && mom < 0 ? 'down' : undefined} />
          <StatCard label="本月营收"
                    value={Number((cur.revenue / 10000).toFixed(1))}
                    prefix="¥" suffix="万"
                    delta={yoy !== null && yoy !== undefined ? `YoY ${(yoy * 100).toFixed(1)}%` : undefined}
                    deltaDir={yoy && yoy > 0 ? 'up' : yoy && yoy < 0 ? 'down' : undefined}
                    valueColor="success" />
          <StatCard label="月底预测"
                    value={Number((prediction / 10000).toFixed(1))}
                    prefix="¥" suffix="万"
                    meta={`客单价 ${formatMoney(avgDeal)}`} />
        </StatsRow>

        {/* 漏斗 + 健康度 */}
        <div className="home-grid">
          <HomeCard title={<>销售漏斗 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>当前阶段快照 · 不受时间筛选影响</span></>}>
            <div className="funnel">
              {(funnel ?? []).map((f: any) => {
                const max = Math.max(1, ...(funnel ?? []).map((x: any) => x.cnt));
                const rate = max > 0 ? Math.round((f.cnt / max) * 100) : 0;
                return (
                  <div key={f.stage} style={{ padding: '4px 0' }}>
                    <FunnelRow
                      stage={f.stage}
                      label={STAGE_LABELS[f.stage] ?? f.stage}
                      count={f.cnt}
                      rate={rate}
                      max={max}
                    />
                    <div style={{
                      paddingLeft: 68, fontSize: 11, color: 'var(--text-3)',
                      display: 'flex', gap: 14, marginTop: -2,
                    }}>
                      <span>平均停留 {f.avgStayDays} 天</span>
                      {f.convToNext !== null && (
                        <span style={{
                          color: f.convToNext >= 0.5 ? 'var(--success)' :
                                 f.convToNext >= 0.3 ? '#fa8c16' : 'var(--danger)',
                        }}>
                          → 下一阶段转化 {(f.convToNext * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {(funnel ?? []).length === 0 && (
                <div style={{ padding: 24, color: 'var(--text-3)', textAlign: 'center' }}>暂无数据</div>
              )}
            </div>
          </HomeCard>

          <HomeCard title={<>健康度分布 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>当前快照</span></>}>
            <div style={{ padding: '12px 16px' }}>
              {(health ?? []).map((h: any) => {
                const max = Math.max(1, ...(health ?? []).map((x: any) => x.cnt));
                const w = (h.cnt / max) * 100;
                return (
                  <div key={h.bucket} style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: HEALTH_COLOR[h.bucket] ?? 'var(--text-2)' }}>{h.bucket}</span>
                      <span style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{h.cnt}</span>
                    </div>
                    <div style={{ background: 'var(--bg-3)', height: 8, borderRadius: 4 }}>
                      <div style={{
                        width: `${w}%`, height: '100%',
                        background: HEALTH_COLOR[h.bucket] ?? 'var(--primary)',
                        borderRadius: 4, transition: 'width 0.3s',
                      }} />
                    </div>
                  </div>
                );
              })}
              {(health ?? []).length === 0 && (
                <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </div>
          </HomeCard>
        </div>

        {/* 渠道 ROI + 月度趋势 */}
        <div className="home-grid">
          {!isManager && (
            <HomeCard title="渠道效果">
              <div style={{ padding: 18 }}>
                {(roi ?? []).length === 0
                  ? <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: 16 }}>暂无数据</div>
                  : (() => {
                    const sorted = [...(roi ?? [])].sort((a, b) => Number(b.total) - Number(a.total));
                    const maxTotal = Math.max(1, ...sorted.map((r) => Number(r.total)));
                    return sorted.map((r) => {
                      const total = Number(r.total);
                      const deals = Number(r.deals);
                      const rate = total > 0 ? (deals / total) * 100 : 0;
                      const w = (total / maxTotal) * 100;
                      return (
                        <div key={r.source ?? '?'} className="hbar-row">
                          <div className="hbar-label">{r.source ?? '未知'}</div>
                          <div className="hbar-track">
                            <div className={`hbar-fill ${rate >= 50 ? 'warm' : ''}`} style={{ width: `${Math.max(2, w)}%` }} />
                          </div>
                          <div className="hbar-val">
                            <span style={{ fontWeight: 500 }}>{formatInt(total)}</span>
                            <span style={{ color: 'var(--text-3)' }}> · {rate.toFixed(0)}%</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
              </div>
            </HomeCard>
          )}

          <HomeCard title="月度营收趋势">
            <div style={{ padding: '14px 18px' }}>
              {(trend?.series ?? []).length === 0
                ? <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: 16 }}>暂无数据</div>
                : (() => {
                  const series = trend.series as any[];
                  const max = Math.max(1, ...series.map((s) => s.revenue));
                  return (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140 }}>
                      {series.map((s, i) => {
                        const h = Math.max(4, (s.revenue / max) * 120);
                        const isLast = i === series.length - 1;
                        return (
                          <div key={s.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                            <div style={{ fontSize: 10, color: 'var(--text-3)' }}>
                              {(s.revenue / 10000).toFixed(1)}万
                            </div>
                            <div style={{
                              width: '80%', height: h,
                              background: isLast ? 'var(--primary)' : 'var(--bg-3)',
                              borderRadius: '4px 4px 0 0',
                            }} />
                            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.month.slice(5)}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
            </div>
          </HomeCard>
        </div>

        {/* 团队对比 */}
        {!isManager && (
          <HomeCard title="团队对比">
            <TableWrap>
              <table className="ck-table">
                <thead>
                  <tr>
                    <th>团队</th>
                    <th style={{ textAlign: 'right' }}>客户数</th>
                    <th style={{ textAlign: 'right' }}>本月成交</th>
                    <th style={{ textAlign: 'right' }}>本月营收</th>
                    <th style={{ textAlign: 'right' }}>转化率</th>
                  </tr>
                </thead>
                <tbody>
                  {(teamCmp ?? []).map((t: any) => (
                    <tr key={t.teamId}>
                      <td>{t.teamName}</td>
                      <td style={{ textAlign: 'right' }}>{formatInt(t.customers)}</td>
                      <td style={{ textAlign: 'right' }}>{formatInt(t.deals)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="money">{formatMoney(t.revenue)}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Pill tone={t.conversionRate >= 0.1 ? 'success' : t.conversionRate >= 0.05 ? 'warning' : 'default'}>
                          {(t.conversionRate * 100).toFixed(2)}%
                        </Pill>
                      </td>
                    </tr>
                  ))}
                  {(teamCmp ?? []).length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>暂无团队</td></tr>
                  )}
                </tbody>
              </table>
            </TableWrap>
          </HomeCard>
        )}

        {/* 销售员多维排行 */}
        <HomeCard title={<>销售员多维排行 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>持客 / 成交 / 转化率 / 平均周期</span></>}>
          <TableWrap>
            <table className="ck-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>排名</th>
                  <th>销售员</th>
                  <th style={{ textAlign: 'right' }}>持客数</th>
                  <th style={{ textAlign: 'right' }}>成交单数</th>
                  <th style={{ textAlign: 'right' }}>营收</th>
                  <th style={{ textAlign: 'right' }}>转化率</th>
                  <th style={{ textAlign: 'right' }}>平均周期</th>
                </tr>
              </thead>
              <tbody>
                {(ranking ?? []).map((r: any, i: number) => (
                  <tr key={r.userId}>
                    <td>
                      <span style={{
                        fontSize: 13, fontWeight: 600,
                        color: i < 3 ? 'var(--warning-text)' : 'var(--text-3)',
                      }}>#{i + 1}</span>
                    </td>
                    <td><CustCell id={r.userId} name={r.name ?? `#${r.userId}`} /></td>
                    <td style={{ textAlign: 'right' }}>{formatInt(r.customers)}</td>
                    <td style={{ textAlign: 'right' }}>{formatInt(r.deals)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="money">{formatMoney(r.revenue)}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Pill tone={r.conversionRate >= 0.1 ? 'success' : r.conversionRate >= 0.05 ? 'warning' : 'default'}>
                        {(r.conversionRate * 100).toFixed(2)}%
                      </Pill>
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>
                      {r.avgCycleDays > 0 ? `${r.avgCycleDays} 天` : '—'}
                    </td>
                  </tr>
                ))}
                {(ranking ?? []).length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>暂无销售员</td></tr>
                )}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>
    </>
  );
}
